/**
 * React hooks for analytics tracking
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import analyticsService from '../services/analyticsService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { findItemById, humanizeSlug } from '../config/navigationConfig';

/**
 * Initialize analytics on app load
 */
export const useAnalyticsInit = () => {
  useEffect(() => {
    analyticsService.init();
    
    // Store first visit timestamp
    if (!localStorage.getItem('vw_first_seen')) {
      localStorage.setItem('vw_first_seen', new Date().toISOString());
    }
  }, []);
};

/**
 * Track page views on route changes
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Map routes to readable page names
    const pageNames = {
      '/': 'Home',
      '/pricing': 'Pricing',
      '/features': 'Features',
      '/solutions': 'Solutions',
      '/contact': 'Contact',
      '/demo': 'Demo Request',
      '/resources': 'Resources',
      '/sign-in': 'Sign In',
      '/privacy-policy': 'Privacy Policy',
    };

    const pageName = pageNames[location.pathname] || location.pathname;
    analyticsService.page(pageName, {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    // Reset scroll tracking for new page
    analyticsService._scrollTracked75 = false;
    analyticsService._scrollTracked90 = false;

  }, [location.pathname]);
};

/**
 * Platform Activity Audit — page-view capture.
 *
 * Fires once per authenticated navigation (route or ?tab=/&subtab= change),
 * reporting "what did this user look at" to the backend Activity Audit feed
 * (backend/routers/auth.py: POST /api/auth/log-page-view). Covers every role
 * — employee, manager, admin — since PlatformActivityMiddleware only captures
 * mutating requests (POST/PUT/PATCH/DELETE), not page views.
 *
 * Deliberately skipped while logged out (marketing/public pages) to avoid
 * noise, and deduped so re-renders on the same URL don't re-log.
 *
 * Also skipped for marketing/public routes even while an authenticated
 * session happens to exist (e.g. a logged-in admin opening "/tryus" or
 * "/pricing" in another tab) — these are unauthenticated top-level pages
 * with no real RBAC module behind them, so logging them previously polluted
 * the Admin Activity "Module" filter with raw marketing-route slugs like
 * "tryus" under Settings Changes.
 */
const MARKETING_PUBLIC_PATHS = [
  '/', '/pricing', '/features', '/solutions', '/resources', '/blog',
  '/case-studies', '/help', '/partner', '/contact', '/compare', '/tools',
  '/tryus', '/try', '/deployment-guide', '/privacy-policy',
  '/security-compliance', '/device-consent', '/signin', '/sso/callback',
  '/signup',
];

const isMarketingPublicPath = (pathname) =>
  MARKETING_PUBLIC_PATHS.includes(pathname) ||
  pathname.startsWith('/tryus') ||
  pathname.startsWith('/try/') ||
  pathname.startsWith('/tools/') ||
  pathname.startsWith('/features/') ||
  pathname.startsWith('/resources/') ||
  pathname.startsWith('/signup/');

// Container routes whose URL child segments are page-INTERNAL sub-tabs, not
// distinct top-level RBAC modules — e.g. AdminDashboard.jsx's own
// `/dashboard/<mainTab>/<subTab...>` split (mainTab = useParams()['*'].split('/')[0]).
// Naively taking the LAST URL segment (as this hook used to, and as
// routers/auth.py:log_page_view still does when `tab` isn't provided) picks
// up the sub-tab id instead — e.g. "/dashboard/application_monitoring/browser"
// resolved to module "browser" instead of "application_monitoring", silently
// misclassifying it under the default "Settings Changes" category instead of
// "Monitoring Config". Mirrored server-side in routers/auth.py's
// `_resolve_page_view_module` for defense-in-depth (e.g. a stale cached bundle).
const NESTED_CONTAINER_MODULES = {
  dashboard: (segments) => segments[1] || 'overview',
  'fake-activity': () => 'fake-activity',
  'super-admin': () => 'super-admin',
  // /profile, /profile/edit, /profile/change-password, /profile/settings are
  // all self-service account pages — collapse to the single "profile"
  // module (see MODULE_TO_CATEGORY) instead of leaking bare sub-page
  // segments like "edit"/"change_password" as if they were top-level modules.
  profile: () => 'profile',
  // /application-monitoring/:tab (e.g. "applications", "browser",
  // "employees", "overview") is its own standalone route (see App.js), not
  // nested under /dashboard — without this entry the naive
  // segments[segments.length - 1] fallback below sends the raw sub-tab name
  // itself as `body.tab`, which WINS over routers/auth.py's own
  // `_resolve_page_view_module()` defense-in-depth (body.tab is checked
  // first), leaking nonsense entries like "applications"/"browser" into the
  // Module filter dropdown instead of the real "application_monitoring"
  // module. Mirrors routers/auth.py's `_NESTED_CONTAINER_MODULES` entry of
  // the same name.
  'application-monitoring': () => 'application_monitoring',
};

const resolvePageViewModuleId = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;
  const resolver = NESTED_CONTAINER_MODULES[segments[0]];
  return resolver ? resolver(segments) : segments[segments.length - 1];
};

export const usePlatformActivityPageView = () => {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useAuth();
  const { t } = useTranslation();
  const lastLoggedRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    if (isMarketingPublicPath(location.pathname)) return;

    const key = `${location.pathname}${location.search}`;
    if (lastLoggedRef.current === key) return;
    lastLoggedRef.current = key;

    const params = new URLSearchParams(location.search);
    // Legacy `?tab=`/`?subtab=` query links are immediately redirected to
    // clean path-based `/dashboard/<tab>` URLs (see AdminDashboard.jsx), so
    // these params are effectively never present anymore for the current
    // routing scheme — kept only for backward compatibility with any
    // lingering old link. The real source of truth is the URL path itself,
    // resolved via resolvePageViewModuleId() so nested sub-tab routes report
    // their top-level module instead of the trailing sub-tab segment.
    const tab = params.get('tab') || params.get('subtab') || resolvePageViewModuleId(location.pathname);

    // Resolve a human-friendly label (e.g. "Monthly Attendance" instead of
    // the raw "monthly-attendance" slug) so the Activity Audit feed reads
    // the same way the sidebar does. Falls back to a title-cased version of
    // the raw ID when it isn't a known nav item (e.g. dynamic routes).
    const idHint = tab;
    const navItem = idHint ? findItemById(idHint) : null;
    const label = navItem ? t(navItem.labelKey) : humanizeSlug(idHint);

    api.post('/api/auth/log-page-view', {
      path: location.pathname,
      tab,
      search: location.search || null,
      label,
    }).catch(() => {
      // Best-effort telemetry — never surface errors to the user.
    });
  }, [location.pathname, location.search, isAuthenticated, currentUser, t]);
};

/**
 * Track CTA clicks
 */
export const useTrackCTA = () => {
  return useCallback((ctaName, location) => {
    analyticsService.trackCTAClick(ctaName, location);
  }, []);
};

/**
 * Track form interactions
 */
export const useFormTracking = (formName) => {
  const onFocus = useCallback(() => {
    analyticsService.trackFormStart(formName);
  }, [formName]);

  const onComplete = useCallback((data) => {
    analyticsService.trackFormComplete(formName, data);
  }, [formName]);

  return { onFocus, onComplete };
};

/**
 * Get visitor data for debugging/display
 */
export const useVisitorData = () => {
  return analyticsService.getVisitorData();
};

/**
 * Track element visibility (for sections, features viewed)
 */
export const useVisibilityTracking = (elementRef, eventName, threshold = 0.5) => {
  useEffect(() => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            analyticsService.track(eventName, {
              element: entry.target.dataset.trackId || eventName,
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [elementRef, eventName, threshold]);
};

/**
 * Track outbound link clicks
 */
export const useOutboundLinkTracking = () => {
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a');
      if (link && link.hostname !== window.location.hostname) {
        analyticsService.track('Outbound Link Clicked', {
          url: link.href,
          text: link.textContent,
        });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
};

export default analyticsService;

